import React, { useEffect, useState } from "react";
import { Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import * as Api from "../../../General/api";
import * as Common from "../../../General/common";

/**
 * Persistent action toolbar rendered inside Facility Dashboard, Provider Dashboard, and CMNs tab.
 * contextType: 'facility' | 'provider' | 'all'
 * contextId:   facilityId or physicianId (0 when contextType = 'all')
 */
function CmnActionButtons({ contextType = "all", contextId = 0 }) {
  const [advancedEnabled, setAdvancedEnabled] = useState(false);
  const [featureLoaded, setFeatureLoaded] = useState(false);
  const [showDownload, setShowDownload] = useState(false);
  const [showDocuSign, setShowDocuSign] = useState(false);
  const [showFax, setShowFax] = useState(false);

  const [pendingCmns, setPendingCmns] = useState([]);
  const [selectedCmnIds, setSelectedCmnIds] = useState([]);
  const [loadingCmns, setLoadingCmns] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [signerEmail, setSignerEmail] = useState("");
  const [signerName, setSignerName] = useState("");
  const CMN_ADVANCED_FEATURE_TASK_ID = 8;

  useEffect(() => {
    Api.getRequest(Api.URI_FeatureSwitch + CMN_ADVANCED_FEATURE_TASK_ID)
      .then((res) => {
        setAdvancedEnabled(res?.data?.result === true);
      })
      .catch(() => {
        setAdvancedEnabled(false);
      })
      .finally(() => {
        setFeatureLoaded(true);
      });
  }, []);

  if (!featureLoaded || !advancedEnabled) {
    return null;
  }

  const loadPendingCmns = () => {
    setLoadingCmns(true);
    setSelectedCmnIds([]);
    setSearchTerm("");
    const facilityId = contextType === "facility" ? contextId : 0;
    const physicianId = contextType === "provider" ? contextId : 0;
    Api.getRequestQry(
      Api.Uri_CmnPendingList,
      `?facilityId=${facilityId}&physicianId=${physicianId}&pageIndex=1&pageSize=200`
    )
      .then((res) => {
        if (res.data.statusCode === 200 && res.data.result) {
          setPendingCmns(res.data.result.items || []);
        }
      })
      .catch((ex) => Common.LogError(ex, "CmnActionButtons", "loadPendingCmns"))
      .finally(() => setLoadingCmns(false));
  };

  const openDownload = () => { loadPendingCmns(); setShowDownload(true); };
  const openDocuSign = () => { loadPendingCmns(); setShowDocuSign(true); };
  const openFax = () => { loadPendingCmns(); setShowFax(true); };

  const toggleSelect = (id) => {
    setSelectedCmnIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = (items) => {
    if (selectedCmnIds.length === items.length) {
      setSelectedCmnIds([]);
    } else {
      setSelectedCmnIds(items.map((c) => c.fC_CMNId));
    }
  };

  const filteredCmns = pendingCmns.filter((c) => {
    const term = searchTerm.toLowerCase();
    return (
      !term ||
      (c.fC_CMNId && String(c.fC_CMNId).includes(term)) ||
      (c.residentName && c.residentName.toLowerCase().includes(term)) ||
      (c.facilityName && c.facilityName.toLowerCase().includes(term)) ||
      (c.physicianName && c.physicianName.toLowerCase().includes(term))
    );
  });

  const handleDownload = async () => {
    if (!selectedCmnIds.length) {
      Common.showSuccessAlert("Please select at least one CMN.", "warning", "Warning!", 3000);
      return;
    }
    for (const id of selectedCmnIds) {
      try {
        const res = await Api.getRequestBlob(Api.Uri_CmnDocumentPreview + id);
        const blob = res.data instanceof Blob ? res.data : new Blob([res.data]);
        const u = URL.createObjectURL(blob);
        window.open(u, "_blank");
        window.setTimeout(() => URL.revokeObjectURL(u), 120000);
      } catch (ex) {
        Common.LogError(ex, "CmnActionButtons", "handleDownload");
        Common.showSuccessAlertAutoClose(`Could not open CMN ${id} preview.`, "warning", "Warning!", 4000);
      }
    }
    setShowDownload(false);
  };

  const handleSendDocuSign = () => {
    if (!selectedCmnIds.length) {
      Common.showSuccessAlert("Please select at least one CMN.", "warning", "Warning!", 3000);
      return;
    }
    if (!signerEmail.trim() || !signerName.trim()) {
      Common.showSuccessAlert("Signer name and signer email are required.", "warning", "Warning!", 3000);
      return;
    }

    const selectedRows = pendingCmns.filter((x) => selectedCmnIds.includes(x.fC_CMNId));
    if (!selectedRows.length) {
      Common.showSuccessAlert("Unable to resolve selected CMN records.", "warning", "Warning!", 3000);
      return;
    }

    let physicianRecipientId = null;
    let facilityRecipientId = null;

    if (contextType === "provider") {
      physicianRecipientId = contextId;
    } else if (contextType === "facility") {
      facilityRecipientId = contextId;
    } else {
      const first = selectedRows[0];
      const allSamePhysician =
        first.physicianId &&
        selectedRows.every((r) => r.physicianId && r.physicianId === first.physicianId);
      const allSameFacility =
        first.facilityId &&
        selectedRows.every((r) => r.facilityId && r.facilityId === first.facilityId);
      if (allSamePhysician) {
        physicianRecipientId = first.physicianId;
      } else if (allSameFacility) {
        facilityRecipientId = first.facilityId;
      } else {
        Common.showSuccessAlert(
          "For CMNs tab selection, choose CMNs belonging to one provider or one facility.",
          "warning",
          "Warning!",
          4000
        );
        return;
      }
    }

    setActionLoading(true);
    Api.postRequest(Api.Uri_CmnDocuSignCreateEnvelope, {
      cmnIds: selectedCmnIds,
      deliveryMethodId: 1,
      physicianRecipientId,
      facilityRecipientId,
      signerEmail: signerEmail.trim(),
      signerName: signerName.trim(),
      emailSubject: `CMN Packet (${selectedCmnIds.length} CMN${selectedCmnIds.length > 1 ? "s" : ""})`,
      sendAsDraft: false,
      isResend: false,
      masterSentGroupId: null,
    })
      .then((res) => {
        if (res.data.success) {
          Common.showSuccessAlertAutoClose("DocuSign envelope sent successfully.", "success", "Success!", 3000);
          setShowDocuSign(false);
        } else {
          Common.showSuccessAlert(res.data.message || "Failed to send DocuSign envelope.", "warning", "Warning!", 3000);
        }
      })
      .catch((ex) => {
        Common.LogError(ex, "CmnActionButtons", "handleSendDocuSign");
        Common.showSuccessAlertAutoClose("An error occurred while sending the DocuSign envelope.", "warning", "Failed!", 3000);
      })
      .finally(() => setActionLoading(false));
  };

  const handleSendFax = () => {
    Common.showSuccessAlert("Send via Fax is coming soon. Backend endpoint is pending.", "info", "Coming Soon!", 3000);
  };

  const resetAndClose = (closer) => {
    setPendingCmns([]);
    setSelectedCmnIds([]);
    setSearchTerm("");
    setSignerEmail("");
    setSignerName("");
    closer(false);
  };

  const renderCmnTable = (items) => (
    <>
      <Form.Control
        type="text"
        placeholder="Search by CMN ID, resident, facility, or provider..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="mb-3"
        size="sm"
      />
      {loadingCmns ? (
        <div className="text-center py-4">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <div style={{ maxHeight: "400px", overflowY: "auto" }}>
          <Table striped bordered hover size="sm" className="w-100">
            <thead>
              <tr>
                <th style={{ width: "40px" }}>
                  <Form.Check
                    type="checkbox"
                    checked={items.length > 0 && selectedCmnIds.length === items.length}
                    onChange={() => toggleSelectAll(items)}
                  />
                </th>
                <th>FC CMN ID</th>
                <th>BT CMN ID</th>
                <th>Resident Name</th>
                <th>Provider</th>
                <th>Facility</th>
                <th>Timely Filing</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center text-muted py-3">
                    No pending CMNs found.
                  </td>
                </tr>
              ) : (
                items.map((cmn) => (
                  <tr key={cmn.fC_CMNId}>
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={selectedCmnIds.includes(cmn.fC_CMNId)}
                        onChange={() => toggleSelect(cmn.fC_CMNId)}
                      />
                    </td>
                    <td>{cmn.fC_CMNId}</td>
                    <td>{cmn.bT_CMNKey || "--"}</td>
                    <td>{cmn.residentName}</td>
                    <td>{cmn.physicianName || "--"}</td>
                    <td>{cmn.facilityName}</td>
                    <td>{cmn.timelyFilingDaysRemaining != null ? cmn.timelyFilingDaysRemaining + " days" : "--"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      )}
    </>
  );

  return (
    <>
      <div className="d-flex mb-3">
        <Button variant="primary" size="sm" className="mr-2" onClick={openDownload}>
          Download CMNs
        </Button>
        <Button variant="success" size="sm" className="mr-2" onClick={openDocuSign}>
          Send via DocuSign
        </Button>
        <Button variant="warning" size="sm" onClick={openFax}>
          Send via Fax
        </Button>
      </div>

      {/* Download CMNs Modal */}
      <Modal
        size="xl"
        show={showDownload}
        onHide={() => resetAndClose(setShowDownload)}
        backdrop="static"
        keyboard={false}
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>Download CMNs</Modal.Title>
        </Modal.Header>
        <Modal.Body>{renderCmnTable(filteredCmns)}</Modal.Body>
        <Modal.Footer>
          <Button variant="danger" size="sm" onClick={() => resetAndClose(setShowDownload)}>
            Close
          </Button>
          <Button variant="primary" size="sm" onClick={handleDownload} disabled={!selectedCmnIds.length}>
            Download CMNs ({selectedCmnIds.length})
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Send via DocuSign Modal */}
      <Modal
        size="xl"
        show={showDocuSign}
        onHide={() => resetAndClose(setShowDocuSign)}
        backdrop="static"
        keyboard={false}
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>Send via DocuSign</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <div className="form-row">
              <div className="col-md-6 mb-2">
                <Form.Label className="mb-1">Signer Name</Form.Label>
                <Form.Control
                  type="text"
                  size="sm"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  placeholder="Enter signer full name"
                />
              </div>
              <div className="col-md-6 mb-2">
                <Form.Label className="mb-1">Signer Email</Form.Label>
                <Form.Control
                  type="email"
                  size="sm"
                  value={signerEmail}
                  onChange={(e) => setSignerEmail(e.target.value)}
                  placeholder="Enter signer email"
                />
              </div>
            </div>
          </div>
          {renderCmnTable(filteredCmns)}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" size="sm" onClick={() => resetAndClose(setShowDocuSign)}>
            Close
          </Button>
          <Button
            variant="success"
            size="sm"
            onClick={handleSendDocuSign}
            disabled={!selectedCmnIds.length || actionLoading}
          >
            {actionLoading ? <Spinner animation="border" size="sm" /> : `Send via DocuSign (${selectedCmnIds.length})`}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Send via Fax Modal */}
      <Modal
        size="xl"
        show={showFax}
        onHide={() => resetAndClose(setShowFax)}
        backdrop="static"
        keyboard={false}
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>Send via Fax</Modal.Title>
        </Modal.Header>
        <Modal.Body>{renderCmnTable(filteredCmns)}</Modal.Body>
        <Modal.Footer>
          <Button variant="danger" size="sm" onClick={() => resetAndClose(setShowFax)}>
            Close
          </Button>
          <Button
            variant="warning"
            size="sm"
            onClick={handleSendFax}
            disabled={!selectedCmnIds.length}
            title="Send via Fax endpoint is pending backend implementation"
          >
            Send via Fax ({selectedCmnIds.length})
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default CmnActionButtons;
